/**
 * adapter.js - Door Knock, as the trial loop sees it.
 *
 * Same interface as every other game's. See
 * `client/src/components/game/shared/trialLoop.js` for what each question is
 * for, and `bubble-pop/adapter.js` for the same answers about a different game.
 */

import { GAME } from './content/index.js';
import { getLevelContent, pacingFor } from './content/levels.js';
import { canonicalPhrase } from './content/creatures.js';
import { judgeAttempt } from './logic/judgeContent.js';
import { buildPrompt, buildHint } from './logic/prompts.js';
import { createRng, planLevel, recentFrom } from '../shared/rounds.js';
import { createLineBag } from '../shared/lineBag.js';
import { requiredCapsFor } from '../../../engine/contract.js';

/**
 * @param {object} [options]
 * @param {number} [options.seed]
 * @param {() => number} [options.rng]
 */
export function createAdapter({ seed = 1, rng } = {}) {
  const random = rng || createRng(seed);
  const bags = {};

  const choose = (key) => (templates) => {
    if (!bags[key] || bags[key].size !== templates.length) {
      bags[key] = createLineBag(templates, { rng: random });
    }
    return bags[key].next();
  };

  let memory = { key: null, previous: null, recent: [] };
  let lastPlace = null;

  /**
   * Where the creature goes, at Phase 4.
   *
   * A level offering two places and always asking for the first turns the
   * preposition back into a fixed part of a memorised string - which is exactly
   * what the level exists to stop. Rotated, avoiding the last one asked for.
   */
  const pickPlace = (content) => {
    const places = content.places || [];
    if (!places.length) return null;
    if (places.length === 1) return places[0];
    const options = places.filter((p) => p !== lastPlace);
    const from = options.length ? options : places;
    lastPlace = from[Math.floor(random() * from.length)];
    return lastPlace;
  };

  return {
    id: GAME.id,
    capabilities: GAME.capabilities,
    coverage: {
      phasesAvailable: GAME.phasesAvailable,
      levelsAvailable: GAME.levelsAvailable,
    },

    has: (phase, level) => Boolean(getLevelContent(phase, level)),
    content: (phase, level) => getLevelContent(phase, level),
    nominalRung: (phase, level) => getLevelContent(phase, level).nominalRung,
    pacing: (phase, level) => pacingFor(getLevelContent(phase, level)),
    requiredCaps: (phase, level) => requiredCapsFor(phase, level, GAME.capabilities),

    plan(phase, level) {
      const content = getLevelContent(phase, level);
      const key = `${phase}.${level}`;
      const same = memory.key === key;
      const rounds = planLevel(content, random, {
        previous: same ? memory.previous : null,
        recent: same ? memory.recent : [],
      });
      memory = {
        key,
        previous: rounds[rounds.length - 1] || null,
        recent: recentFrom(rounds),
      };
      // The shared planner speaks in objects and carriers; this game's words
      // for them are creatures, verbs and places.
      return rounds.map((round) => ({
        ...round,
        targets: round.targets.map((target) => ({
          ...target,
          creatureId: target.objectId,
          verbId: content.verbs ? content.verbs[0] : null,
          placeId: pickPlace(content),
          places: content.places || null,
        })),
      }));
    },

    phraseFor: (target) => canonicalPhrase(target),
    onScreen: (target) => target.onScreen || [target.creatureId],
    gridState: () => null,
    targetPosition: (target) => `door-${(target.onScreen || []).indexOf(target.creatureId)}`,
    optionsVisible: (phase, rungId) => phase === 3 && rungId !== 'gestural',

    prompt: ({ phase, rungId, target, content }) => buildPrompt({
      phase, rungId, target, content, choose: choose(`p${phase}-${rungId}`),
    }),

    hint: ({ phase, target, content }) => buildHint({
      phase, target, content, choose: choose(`hint-${phase}`),
    }),

    judge: ({ phase, target, trial, transcript, alternatives }) => judgeAttempt({
      phase,
      target,
      trial,
      transcript,
      alternatives,
      choices: {
        creatures: target.onScreen || [target.creatureId],
        places: target.places || undefined,
      },
    }),
  };
}

export default { createAdapter };
